import type {
  ApiSuccessEnvelope,
  PriceCheckerDevice,
  PriceCheckerQuote,
} from '@pos/types';

import { getRuntimeConfig } from '../../lib/runtimeConfig';
export class PriceCheckerError extends Error {
  constructor(public status: number) {
    super(`Price checker request failed (${status})`);
  }
}
async function read<T>(
  path: string,
  token: string,
  signal?: AbortSignal,
): Promise<T> {
  const timeout = new AbortController();
  const abort = () => timeout.abort();
  signal?.addEventListener('abort', abort, { once: true });
  if (signal?.aborted) timeout.abort();
  const timer = setTimeout(abort, 8000);
  try {
    const { apiBaseUrl } = getRuntimeConfig(import.meta.env);
    const response = await fetch(`${apiBaseUrl}${path}`, {
      headers: { Authorization: `Bearer ${token}` },
      cache: 'no-store',
      signal: timeout.signal,
    });
    if (!response.ok) throw new PriceCheckerError(response.status);
    const body = (await response.json()) as ApiSuccessEnvelope<T>;
    if (!body.ok) throw new PriceCheckerError(503);
    return body.data;
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener('abort', abort);
  }
}
export const priceCheckerApi = {
  context: (token: string, signal?: AbortSignal) =>
    read<PriceCheckerDevice>('/price-checker/context', token, signal),
  lookup: (token: string, barcode: string, signal?: AbortSignal) =>
    read<PriceCheckerQuote>(
      `/price-checker/lookup?${new URLSearchParams({ barcode })}`,
      token,
      signal,
    ),
};
